import { PROJECT_FORMAT_VERSION, type Project } from '@/domain/types';
import { LoadReport, isRecord, readInt } from './validate';
import { parseProject } from './parse';

/**
 * Project format migration.
 *
 * A `.dawproject` written by any released build must open in any later build.
 * The rule: every format bump adds one entry to `MIGRATIONS`, keyed by the
 * version it upgrades *from*. Migrations operate on raw JSON, not on `Project`,
 * because the shape they start from is by definition not the current shape.
 *
 * Files newer than this build are refused rather than guessed at — silently
 * discarding fields the user's newer build wrote would lose their work.
 */

export type RawProject = Record<string, unknown>;
export type Migration = (raw: RawProject, report: LoadReport) => RawProject;

export const MIGRATIONS: Record<number, Migration> = {
  /**
   * Version 0 covers pre-release drafts that predate the version stamp. They
   * are structurally compatible with v1; the parser's repair pass handles the
   * differences, so this step only stamps the version.
   */
  0: (raw) => ({ ...raw, meta: { ...(isRecord(raw.meta) ? raw.meta : {}), formatVersion: 1 } }),
};

export class UnsupportedProjectVersionError extends Error {
  constructor(readonly fileVersion: number) {
    super(
      `This project was saved by a newer version of Stage Zero (format v${fileVersion}; ` +
        `this build understands up to v${PROJECT_FORMAT_VERSION}). Update Stage Zero to open it.`,
    );
    this.name = 'UnsupportedProjectVersionError';
  }
}

export function readFormatVersion(raw: unknown): number {
  if (!isRecord(raw)) return 0;
  const meta = isRecord(raw.meta) ? raw.meta : {};
  return readInt(meta.formatVersion ?? raw.formatVersion, 0, 0, 1_000_000);
}

/**
 * Brings raw project JSON up to the current format and validates it.
 * Throws `UnsupportedProjectVersionError` for files from the future.
 */
export function migrateAndParse(raw: unknown, report = new LoadReport()): { project: Project; report: LoadReport } {
  let version = readFormatVersion(raw);
  if (version > PROJECT_FORMAT_VERSION) throw new UnsupportedProjectVersionError(version);

  let current: RawProject = isRecord(raw) ? { ...raw } : {};
  while (version < PROJECT_FORMAT_VERSION) {
    const migration = MIGRATIONS[version];
    if (!migration) {
      report.warn(`No migration from format v${version}; the project was loaded with best-effort repairs.`);
      break;
    }
    current = migration(current, report);
    const next = readFormatVersion(current);
    if (next <= version) {
      report.warn(`Migration from v${version} did not advance the format version; stopping.`);
      break;
    }
    report.warn(`Upgraded project format v${version} to v${next}.`);
    version = next;
  }

  return parseProject(current, report);
}
